import type { TFunction } from 'i18next'

import type { LocalizedString } from '@/ducks/common.schema'
import type { ApiFetchMenu, ApiMenuItem } from '@/ducks/menu/menu.types'

import {
  getCampaignExpirationText,
  getFlatAmountOffLabel
} from './campaigns.helpers'
import type {
  CampaignItemDetail,
  FoodOfferCampaign,
  FoodOfferDiscountType
} from './campaigns.types'

const MS_PER_DAY = 24 * 60 * 60 * 1000

type TranslateFn = (value: LocalizedString) => string

const parseExpiryToTimestamp = (
  value: string | number | null | undefined
): number | null => {
  if (value == null || value === '') return null

  if (typeof value === 'number') {
    return value > 0 ? value : null
  }

  const numeric = Number(value)
  if (!Number.isNaN(numeric) && numeric > 0) {
    return numeric
  }

  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

export const buildFoodOfferCategoryPath = (
  categoryName: LocalizedString,
  subCategoryName: LocalizedString | undefined,
  getTranslation: TranslateFn
): string => {
  const category = getTranslation(categoryName)
  if (!subCategoryName) return category
  return `${category} / ${getTranslation(subCategoryName)}`
}

export const findFoodOfferMenuItem = (
  menu: ApiFetchMenu | undefined,
  foodId: string,
  getTranslation: TranslateFn
): { item: ApiMenuItem; categoryPath: string } | null => {
  if (!menu?.menu?.mainCategories?.length) return null

  for (const category of menu.menu.mainCategories) {
    for (const item of category.items) {
      if (item._id === foodId) {
        return {
          item,
          categoryPath: buildFoodOfferCategoryPath(
            category.name,
            undefined,
            getTranslation
          )
        }
      }
    }

    for (const subCategory of category.subCategories) {
      for (const item of subCategory.items) {
        if (item._id === foodId) {
          return {
            item,
            categoryPath: buildFoodOfferCategoryPath(
              category.name,
              subCategory.name,
              getTranslation
            )
          }
        }
      }
    }
  }

  return null
}

/** The menu item's discount type; an older backend sends none, which means percentage. */
const resolveDiscountType = (
  menuItem: ApiMenuItem,
  discountType?: FoodOfferDiscountType | null
): FoodOfferDiscountType =>
  (discountType ?? menuItem.offer?.discount_type) === 'flat'
    ? 'flat'
    : 'percentage'

/** A flat amount comes off the price as is (never below 0); a percentage is a share of it. */
const getDiscountedPrice = (
  price: number,
  amount: number,
  discountType: FoodOfferDiscountType
): number => {
  if (amount <= 0) return price
  if (discountType === 'flat') return Math.max(0, price - amount)
  return Math.round(price * (1 - amount / 100))
}

export const mapFoodOfferMenuItemToCampaignDetail = (
  menuItem: ApiMenuItem,
  categoryPath: string,
  getTranslation: TranslateFn,
  discountType?: FoodOfferDiscountType | null
): CampaignItemDetail => {
  const originalPrice = menuItem.price
  const discountedPrice = getDiscountedPrice(
    originalPrice,
    menuItem.offer?.amount ?? 0,
    resolveDiscountType(menuItem, discountType)
  )

  return {
    id: menuItem._id,
    name: getTranslation(menuItem.name),
    categoryPath,
    image: menuItem.avatar,
    originalPrice,
    discountedPrice
  }
}

export const getFoodOfferExpirationText = (
  menuItem: ApiMenuItem,
  t: TFunction,
  now = Date.now(),
  discountType?: FoodOfferDiscountType | null
): string => {
  const expiry =
    parseExpiryToTimestamp(menuItem.offer?.offer_valid_until) ??
    parseExpiryToTimestamp(menuItem.offer?.offer_validity)

  // EXSR-8094: a flat discount with no end date reads "Unlimited"; a percentage one keeps "--".
  if (
    expiry == null &&
    resolveDiscountType(menuItem, discountType) === 'flat'
  ) {
    return t('marketing.campaigns.unlimited')
  }

  if (expiry == null || expiry < now) {
    return '--'
  }

  const daysLeft = Math.max(1, Math.ceil((expiry - now) / MS_PER_DAY))
  return t('marketing.campaigns.days-left', { count: daysLeft })
}

export const getFoodOfferDiscountLabel = (
  menuItem: ApiMenuItem,
  t: TFunction,
  currency = '',
  discountType?: FoodOfferDiscountType | null
): string | null => {
  if (menuItem.offer?.amount == null) return null

  if (resolveDiscountType(menuItem, discountType) === 'flat') {
    return getFlatAmountOffLabel(menuItem.offer.amount, currency, t)
  }

  return t('marketing.campaigns.percent-off', {
    value: menuItem.offer.amount
  })
}

export const getFoodOfferFoodId = (campaign: FoodOfferCampaign): string =>
  campaign.item?.id ?? campaign.campaignId

export const getFoodOfferCampaignFallbackExpiration = (
  campaign: FoodOfferCampaign,
  t: TFunction,
  now = Date.now()
): string => getCampaignExpirationText(campaign, t, now)
